import { useMemo, useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useAuth from "@/hooks/use-auth";
import useFeatureFlag, { CLAIM_BUSINESS } from "@/hooks/use-feature-flag";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { cn } from "@/lib/utils";

import DesktopMenu from "./desktop-menu";
import { MenuIcon } from "./icons";
import {
  AddGame,
  Articles,
  Blog,
  BlogHome,
  // Call,
  Chat,
  Cities,
  ClaimBusiness,
  Dashboard,
  Discord,
  ExploreBusiness,
  Feedback,
  GiftCard,
  Login,
  Logout,
  Meetup,
  type MenuItemGroupType,
  MyProfile,
  News,
  Operators,
  Signup,
} from "./menu-items";
import MobileMenu from "./mobile-menu";

interface AuthMenuProps {
  className?: string;
  isExplorePage?: boolean;
  isHomepage?: boolean;
}

export default function AuthMenu({
  className,
  isExplorePage = false,
  isHomepage = false,
}: AuthMenuProps) {
  const { me } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const isClaimBusinessEnabled = useFeatureFlag(CLAIM_BUSINESS);
  const isDesktop = useIsBreakPoint("md");

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: its fine
  const menuItems = useMemo(() => {
    const items: MenuItemGroupType[] = [];

    const blog = {
      content: <Blog />,
      hide: isHomepage ? "lg" : undefined,
      key: "blog",
    };
    // const call = { content: <Call />, hide: isHomepage ? 'lg' : undefined, key: 'call' };
    const chat = { content: <Chat />, key: "chat" };
    const cities = { content: <Cities />, key: "cities" };
    const discord = { content: <Discord />, key: "discord" };
    const giftCard = { content: <GiftCard />, key: "gift-card" };
    const meetup = { content: <Meetup />, key: "meetup" };
    const businessItems = [
      { content: <ClaimBusiness />, key: "business/claim" },
      { content: <Dashboard />, key: "business/dashboard" },
      { content: <ExploreBusiness />, key: "business/explore" },
    ];

    if (me) {
      items.push([
        { content: <MyProfile user={me} />, key: "my-profile" },
        { content: <AddGame userId={me.id} />, key: "add-game" },
        chat,
        // call,
        { content: <Feedback />, key: "feedback" },
        discord,
        meetup,
        giftCard,
      ]);

      if (isClaimBusinessEnabled) {
        items.push({
          hide: isHomepage ? "lg" : undefined,
          items: businessItems,
        });
      }

      items.push([cities, blog]);

      items.push([{ content: <Logout />, key: "logout" }]);
    } else {
      items.push([
        { content: <Login />, key: "login" },
        { content: <Signup />, key: "signup" },
      ]);

      if (isClaimBusinessEnabled) {
        items.push({
          hide: isHomepage ? "lg" : undefined,
          items: businessItems,
        });
      }

      items.push([chat, discord, meetup, giftCard]);

      if (isExplorePage) {
        items.push([cities, blog]);
      } else {
        items.push([cities]);
        items.push([
          {
            content: <Articles />,
            hide: isHomepage ? "lg" : undefined,
            key: "articles",
          },
          {
            content: <News />,
            hide: isHomepage ? "lg" : undefined,
            key: "news",
          },
          {
            content: <Operators />,
            hide: isHomepage ? "lg" : undefined,
            key: "operators",
          },
          {
            content: <BlogHome />,
            hide: isHomepage ? "lg" : undefined,
            key: "blog-home",
          },
        ]);
      }
    }

    return items;
  }, [isClaimBusinessEnabled, isExplorePage, isHomepage, me]);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label="Menu"
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full border border-white/20 shadow-white transition-shadow hover:shadow-0-0-10",
          className
        )}
      >
        <MenuIcon />
      </PopoverTrigger>
      {isDesktop ? (
        <PopoverContent
          align="end"
          className="w-auto min-w-86 border-0 bg-transparent p-0 shadow-none"
        >
          <DesktopMenu items={menuItems} />
        </PopoverContent>
      ) : (
        <MobileMenu items={menuItems} onOpenChange={setIsOpen} open={isOpen} />
      )}
    </Popover>
  );
}
